import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Droplets, Check, Minus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GLASS_SIZE = 250; // ml per glass
const DEFAULT_GOAL = 2000; // ml

export default function WaterPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [intake, setIntake] = useState(0);
  const [history, setHistory] = useState([]);
  const [goal, setGoal] = useState(DEFAULT_GOAL);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (hasAccess) {
      loadData();
    }
  }, [hasAccess]);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/api/water-logs`, {
        headers: getAuthHeader()
      });
      const { logs, goal: savedGoal } = res.data;
      
      setGoal(savedGoal || DEFAULT_GOAL);
      setHistory(logs || []);
      
      // Find today's intake
      const todayLog = logs?.find(log => log.date === today);
      if (todayLog) {
        setIntake(todayLog.intake || 0);
      }
    } catch (err) {
      console.error('Failed to load water data:', err);
      toast.error('Kunde inte ladda vattendata');
    } finally {
      setLoading(false);
    }
  };

  const saveIntake = async (newIntake) => {
    try {
      await axios.post(`${API}/api/water-logs`, {
        date: today,
        intake: newIntake
      }, {
        headers: getAuthHeader()
      });
    } catch (err) {
      console.error('Failed to save water intake:', err);
      toast.error('Kunde inte spara');
    }
  };

  const addWater = async (amount = GLASS_SIZE) => {
    const newIntake = intake + amount;
    setIntake(newIntake);
    await saveIntake(newIntake);
    
    if (newIntake >= goal && intake < goal) {
      toast.success('🎉 Dagens mål uppnått!');
    }
  };

  const removeWater = async () => {
    if (intake > 0) {
      const newIntake = Math.max(0, intake - GLASS_SIZE);
      setIntake(newIntake);
      await saveIntake(newIntake);
    }
  };

  const glasses = Math.floor(intake / GLASS_SIZE);
  const progress = Math.min(100, (intake / goal) * 100);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="water-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Vattenkoll</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att spåra ditt vattenintag.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white" data-testid="water-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full w-9 h-9 p-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Vattenkoll</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-8 flex justify-center">
          <div className="w-12 h-12 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white" data-testid="water-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Vattenkoll</h1>
            <p className="text-xs text-muted-foreground">{intake} ml av {goal} ml</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Main display */}
        <div className="text-center space-y-6">
          {/* Water drop visualization */}
          <div className="relative w-48 h-48 mx-auto">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {/* Background drop */}
              <path
                d="M50 5 C50 5 15 45 15 65 C15 85 30 95 50 95 C70 95 85 85 85 65 C85 45 50 5 50 5"
                fill="#e0f2fe"
                stroke="#bae6fd"
                strokeWidth="2"
              />
              {/* Water fill */}
              <clipPath id="dropClip">
                <path d="M50 5 C50 5 15 45 15 65 C15 85 30 95 50 95 C70 95 85 85 85 65 C85 45 50 5 50 5" />
              </clipPath>
              <rect
                x="0"
                y={100 - progress}
                width="100"
                height={progress}
                fill="url(#waterGradient)"
                clipPath="url(#dropClip)"
                className="transition-all duration-500"
              />
              <defs>
                <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#38bdf8" />
                  <stop offset="100%" stopColor="#0284c7" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-sky-600">{Math.round(progress)}%</span>
            </div>
          </div>

          {/* Stats */}
          <div className="space-y-2">
            <p className="text-4xl font-bold text-sky-600">{intake} ml</p>
            <p className="text-muted-foreground">
              {glasses} glas ({GLASS_SIZE} ml/glas)
            </p>
            {intake >= goal && (
              <p className="text-emerald-600 font-medium flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                Dagens mål uppnått!
              </p>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={removeWater}
            className="rounded-full w-14 h-14 p-0"
            disabled={intake === 0}
            data-testid="remove-water-btn"
          >
            <Minus className="w-6 h-6" />
          </Button>
          
          <Button
            size="lg"
            onClick={() => addWater()}
            className="rounded-full w-20 h-20 p-0 bg-sky-500 hover:bg-sky-600"
            data-testid="add-water-btn"
          >
            <Droplets className="w-8 h-8" />
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            onClick={() => addWater()}
            className="rounded-full w-14 h-14 p-0"
            data-testid="add-water-plus-btn"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>

        {/* Quick add */}
        <div className="grid grid-cols-4 gap-2">
          {[100, 250, 500, 750].map(amount => (
            <Button
              key={amount}
              variant="outline"
              onClick={() => addWater(amount)}
              className="rounded-xl h-12"
              data-testid={`add-water-${amount}`}
            >
              +{amount} ml
            </Button>
          ))}
        </div>

        {/* Weekly overview */}
        <div className="bg-white rounded-2xl p-4 border border-border/30">
          <h3 className="font-semibold mb-3">Senaste 7 dagarna</h3>
          <div className="flex justify-between gap-1">
            {Array.from({ length: 7 }).map((_, i) => {
              const date = new Date();
              date.setDate(date.getDate() - (6 - i));
              const dateStr = date.toISOString().split('T')[0];
              const dayHistory = history.find(h => h.date === dateStr);
              const dayIntake = dateStr === today ? intake : (dayHistory?.intake || 0);
              const dayProgress = Math.min(100, (dayIntake / goal) * 100);
              const dayName = date.toLocaleDateString('sv-SE', { weekday: 'short' }).slice(0, 2);
              
              return (
                <div key={i} className="flex-1 text-center">
                  <div className="h-20 bg-sky-100 rounded-lg relative overflow-hidden mb-1">
                    <div 
                      className="absolute bottom-0 left-0 right-0 bg-sky-400 transition-all"
                      style={{ height: `${dayProgress}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{dayName}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tips */}
        <div className="bg-sky-50 rounded-2xl p-4 border border-sky-100">
          <p className="text-sky-700 text-sm">
            💡 <strong>Tips:</strong> Drick ett glas vatten direkt när du vaknar och innan varje måltid.
          </p>
        </div>
      </main>
    </div>
  );
}
